import { MediaSource } from '@/shared/types';
import {
  getMediaSourceUrlKey,
  getMediaType,
  junkdrawerDocumentApiPathFromUrl,
  parseCitationPageFromMedia,
} from '@/shared/functions';

/**
 * Last junkdrawer viewing page for a citation identity
 * (URL + apiPath + citation page), plus an optional event/slide scope.
 *
 * Survives regular ↔ fullscreen PdfViewer remounts (minimize, Escape, other
 * setIsFullscreen(false) paths). New citation/source keys start at their
 * citation page; paging updates the remembered page for that key.
 * Without a scope, every event that cites the same file and page shares one
 * slot, so paging one event moves the others. A slide can also hold several
 * copies of one file that cite different pages, so timeline viewers pass
 * ``slideMediaInstanceScope`` (slide id plus the media index on that slide).
 */
const viewingPageByMemoryKey = new Map<string, number>();

/** Copied onto a fullscreen media clone so the remounted viewer uses the same scope. */
const VIEWING_PAGE_SCOPE_FIELD = 'viewingPageScope';

export function viewingPageScopeFromMedia(media: MediaSource | null | undefined): string | undefined {
  if (!media) return undefined;
  const raw = (media as MediaSource & { viewingPageScope?: unknown }).viewingPageScope;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
}

/**
 * One clip on a slide. Index 0 is the card; later indexes are the other files
 * on that same event. Matches the capture scope MediaPane already stamps.
 */
export function slideMediaInstanceScope(
  slideId: string | undefined,
  mediaIndex: number,
): string | undefined {
  const id = slideId?.trim();
  if (!id) return undefined;
  const index = Number.isInteger(mediaIndex) && mediaIndex >= 0 ? mediaIndex : 0;
  return `htl:${id}:${index}`;
}

/** Stamp a slide/event scope onto a media clone. Not written back to the event. */
export function stampViewingPageScope(media: MediaSource, scope: string | undefined): void {
  const trimmed = scope?.trim();
  if (!trimmed) return;
  (media as MediaSource & { viewingPageScope?: string })[VIEWING_PAGE_SCOPE_FIELD] = trimmed;
}

/** In-progress find-on-page state keyed like ``viewingPageByMemoryKey``. */
export interface PdfSearchHandoffState {
  searchQuery: string;
  activeMatchIndex: number;
}

const searchStateByMemoryKey = new Map<string, PdfSearchHandoffState>();

export function getPdfViewerPageMemoryKey(
  media: MediaSource,
  apiPath: string,
  citationPage: number,
  viewingPageScope?: string,
): string {
  const base = `${getMediaSourceUrlKey(media)}|${apiPath}|c:${citationPage}`;
  const scope = viewingPageScope?.trim() || viewingPageScopeFromMedia(media);
  return scope ? `${base}|e:${scope}` : base;
}

function apiPathForMedia(media: MediaSource): 'pdf' | 'ms-word' | 'ms-powerpoint' {
  const type = getMediaType(media);
  const fallback = type === 'docx' ? 'ms-word' : type === 'pptx' ? 'ms-powerpoint' : 'pdf';
  return junkdrawerDocumentApiPathFromUrl(
    media.srcToUse || media.mediaSrc || media.mediaUrl,
    fallback,
  );
}

/**
 * Page to open when a mounted viewer’s memory key changes.
 *
 * A scoped slot (one event or slide) keeps a page the user already chose.
 * A slot with no memory, and any unscoped citation switch, opens on the
 * citation page.
 */
export function resolveViewingPageOnMemoryKeyChange(
  citationPage: number,
  rememberedPage: number | undefined,
  hasViewingScope: boolean,
): number {
  if (hasViewingScope && typeof rememberedPage === 'number' && rememberedPage >= 1) {
    return rememberedPage;
  }
  return citationPage >= 1 ? citationPage : 1;
}

/**
 * Page to show the first time a viewer mounts.
 *
 * A `page` prop is the parent's explicit choice. The citation page is not:
 * converted-document cards pass it whenever they have no stored page, and
 * persisting that on mount erases the page already remembered for the event.
 */
export function initialPdfViewerPage(
  isTitleSlide: boolean,
  citationPage: number,
  rememberedPage: number | undefined,
  hasViewingScope: boolean,
  controlledPage?: number | null,
): number {
  if (isTitleSlide) return 1;
  const citation = citationPage >= 1 ? citationPage : 1;
  const remembered =
    typeof rememberedPage === 'number' && rememberedPage >= 1 ? rememberedPage : undefined;
  const controlled =
    typeof controlledPage === 'number' && controlledPage >= 1 ? controlledPage : undefined;
  if (controlled == null) {
    return remembered ?? citation;
  }
  if (hasViewingScope && remembered != null && remembered !== controlled && controlled === citation) {
    return remembered;
  }
  return controlled;
}

/**
 * True when publishing `pageNumber` would replace a scoped remembered page
 * with the citation default. An explicit non-citation `page` prop is the
 * parent's choice and must still be published.
 */
export function citationFallbackWouldReplaceScopedPage(
  citationPage: number,
  rememberedPage: number | undefined,
  pageNumber: number,
  hasViewingScope: boolean,
  controlledPage?: number | null,
): boolean {
  if (!hasViewingScope) return false;
  const citation = citationPage >= 1 ? citationPage : 1;
  const remembered =
    typeof rememberedPage === 'number' && rememberedPage >= 1 ? rememberedPage : undefined;
  if (remembered == null || remembered === pageNumber || pageNumber !== citation) return false;
  if (typeof controlledPage === 'number' && controlledPage >= 1 && controlledPage !== citation) {
    return false;
  }
  return true;
}

/** Last viewed page for this citation key, if any. */
export function getRememberedViewingPage(memoryKey: string): number | undefined {
  const page = viewingPageByMemoryKey.get(memoryKey);
  return typeof page === 'number' && page >= 1 ? page : undefined;
}

/** Remember the page the user is currently viewing for this citation key. */
export function rememberViewingPage(memoryKey: string, page: number): void {
  if (memoryKey && page >= 1) {
    viewingPageByMemoryKey.set(memoryKey, page);
  }
}

/** Drop memory for one key (or all keys when omitted). */
export function clearRememberedViewingPage(memoryKey?: string): void {
  if (memoryKey) {
    viewingPageByMemoryKey.delete(memoryKey);
  } else {
    viewingPageByMemoryKey.clear();
  }
}

/** Remember viewing page for a media item (e.g. MediaDisplay before a portal remount). */
export function rememberViewingPageForMedia(
  media: MediaSource | null | undefined,
  viewingPage: number | null | undefined,
  viewingPageScope?: string,
): void {
  if (!media) {
    return;
  }
  const page =
    typeof viewingPage === 'number' && viewingPage >= 1
      ? viewingPage
      : parseCitationPageFromMedia(media);
  const citationPage = parseCitationPageFromMedia(media);
  rememberViewingPage(
    getPdfViewerPageMemoryKey(media, apiPathForMedia(media), citationPage, viewingPageScope),
    page,
  );
}

/** Last in-progress find-on-page state for this citation key, if any. */
export function getRememberedPdfSearchState(memoryKey: string): PdfSearchHandoffState | undefined {
  const state = searchStateByMemoryKey.get(memoryKey);
  if (!state?.searchQuery.trim()) {
    return undefined;
  }
  return state;
}

/** Remember find-on-page query + active hit for this citation key. */
export function rememberPdfSearchState(memoryKey: string, state: PdfSearchHandoffState): void {
  if (!memoryKey) {
    return;
  }
  if (!state.searchQuery.trim()) {
    searchStateByMemoryKey.delete(memoryKey);
    return;
  }
  searchStateByMemoryKey.set(memoryKey, {
    searchQuery: state.searchQuery,
    activeMatchIndex: Math.max(0, Math.floor(state.activeMatchIndex)),
  });
}

/** Drop in-progress search memory for one key (or all keys when omitted). */
export function clearRememberedPdfSearchState(memoryKey?: string): void {
  if (memoryKey) {
    searchStateByMemoryKey.delete(memoryKey);
  } else {
    searchStateByMemoryKey.clear();
  }
}

/** Remember find-on-page state before a portal remount (e.g. maximize / minimize). */
export function rememberPdfSearchForMedia(
  media: MediaSource | null | undefined,
  state: PdfSearchHandoffState | null | undefined,
  viewingPageScope?: string,
): void {
  if (!media || !state?.searchQuery.trim()) {
    return;
  }
  const citationPage = parseCitationPageFromMedia(media);
  rememberPdfSearchState(
    getPdfViewerPageMemoryKey(media, apiPathForMedia(media), citationPage, viewingPageScope),
    state,
  );
}
